import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthContext } from '../contexts/AuthContext';
import { getUserProjects, createProject, deleteProject, renameProject } from '../firebase/projects';
import { deserializeProject } from '../utils/projectSerializer';
import { fetchProjects } from '../firebase/boreholeBrowser';
import { fetchExternalCrossSections, fetchExternalCrossSection } from '../firebase/externalCrossSections';

export default function ProjectListPage() {
  const { user, logout } = useAuthContext();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);

  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [renamingId, setRenamingId] = useState(null);
  const [renameValue, setRenameValue] = useState('');
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [createValue, setCreateValue] = useState('Nowy przekroj');

  // Modal "Wczytaj z portalu" — dwustopniowy: projekt -> przekroj
  const [showPortalDialog, setShowPortalDialog] = useState(false);
  const [portalProjects, setPortalProjects] = useState([]);
  const [portalProjectsLoading, setPortalProjectsLoading] = useState(false);
  const [portalProjectsError, setPortalProjectsError] = useState(null);
  const [portalProjectFilter, setPortalProjectFilter] = useState('');
  const [selectedPortalProject, setSelectedPortalProject] = useState(null);
  const [portalCrossSections, setPortalCrossSections] = useState([]);
  const [portalCSLoading, setPortalCSLoading] = useState(false);
  const [portalCSError, setPortalCSError] = useState(null);
  const [openingCS, setOpeningCS] = useState(null); // id przekroju ktory wlasnie kopiujemy

  const loadProjects = async () => {
    if (!user) return;
    setLoading(true);
    try {
      const list = await getUserProjects(user.uid);
      setProjects(list);
    } catch (err) {
      console.error('Blad ladowania projektow:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadProjects();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const handleCreateProject = () => {
    setCreateValue('Nowy przekroj');
    setShowCreateDialog(true);
  };

  const handleConfirmCreate = () => {
    const trimmed = createValue.trim();
    if (!trimmed) return;
    setShowCreateDialog(false);
    navigate('/browse', { state: { pendingProject: { name: trimmed } } });
  };

  const handleDeleteProject = async (projectId, projectName) => {
    if (!window.confirm(`Czy na pewno chcesz usunac przekroj "${projectName}"?`)) return;
    try {
      await deleteProject(projectId);
      setProjects(prev => prev.filter(p => p.id !== projectId));
    } catch (err) {
      console.error('Blad usuwania projektu:', err);
      alert('Nie udalo sie usunac projektu.');
    }
  };

  const handleStartRename = (projectId, currentName) => {
    setRenamingId(projectId);
    setRenameValue(currentName);
  };

  const handleRename = async (projectId) => {
    if (!renameValue.trim()) return;
    try {
      await renameProject(projectId, renameValue.trim());
      setProjects(prev => prev.map(p =>
        p.id === projectId ? { ...p, name: renameValue.trim() } : p
      ));
    } catch (err) {
      console.error('Blad zmiany nazwy:', err);
    }
    setRenamingId(null);
  };

  const handleOpenPortalDialog = async () => {
    setShowPortalDialog(true);
    setSelectedPortalProject(null);
    setPortalCrossSections([]);
    setPortalCSError(null);
    setPortalProjectFilter('');
    if (portalProjects.length > 0) return;
    setPortalProjectsLoading(true);
    setPortalProjectsError(null);
    try {
      const list = await fetchProjects();
      // Sort po tytule
      list.sort((a, b) => (a.title || '').localeCompare(b.title || '', 'pl'));
      setPortalProjects(list);
    } catch (err) {
      console.error('Blad ladowania projektow portalu:', err);
      setPortalProjectsError('Nie udalo sie pobrac projektow z portalu (sprawdz uprawnienia).');
    } finally {
      setPortalProjectsLoading(false);
    }
  };

  const handleSelectPortalProject = async (project) => {
    setSelectedPortalProject(project);
    setPortalCSLoading(true);
    setPortalCSError(null);
    setPortalCrossSections([]);
    try {
      console.log(`[Portal] Czytam crossSection z currentProjects/${project.id}`);
      const list = await fetchExternalCrossSections(project.id);
      console.log(`[Portal] Znaleziono ${list.length} przekrojow w projekcie ${project.id}`);
      // Sort po modified malejaco (najnowsze na gorze), fallback na name
      list.sort((a, b) => {
        const ta = a.modified?.seconds || a.modified?.toMillis?.() / 1000 || 0;
        const tb = b.modified?.seconds || b.modified?.toMillis?.() / 1000 || 0;
        if (tb !== ta) return tb - ta;
        return (a.name || '').localeCompare(b.name || '', 'pl');
      });
      setPortalCrossSections(list);
    } catch (err) {
      console.error('Blad ladowania przekrojow portalu:', err);
      const code = err?.code || '';
      const msg = err?.message || String(err);
      if (code === 'permission-denied') {
        setPortalCSError(`Brak uprawnien do czytania crossSections w tym projekcie (rules Firestore).`);
      } else {
        setPortalCSError(`Nie udalo sie pobrac przekrojow: ${msg}`);
      }
    } finally {
      setPortalCSLoading(false);
    }
  };

  const handleOpenPortalCrossSection = async (cs) => {
    if (!user || !selectedPortalProject) return;
    setOpeningCS(cs.id);
    try {
      const fullDoc = await fetchExternalCrossSection(selectedPortalProject.id, cs.id, cs._sub);
      if (!fullDoc) throw new Error('Dokument nie istnieje');
      if (!Array.isArray(fullDoc.items) || fullDoc.items.length === 0) {
        throw new Error('Przekroj nie zawiera zadnych otworow');
      }

      // Sortuj items po `order` i odrzuc szmaciane wpisy (bez boreholeId).
      // Konwertuj na format handoffu — BoreholeBrowser ma `applyHandoff` ktora
      // oczekuje `items[].infoBoreholeId`, `currentProjectId`, `projectName`, `distance`.
      const sortedItems = [...fullDoc.items].sort((a, b) => {
        const oa = a.order != null ? Number(a.order) : 0;
        const ob = b.order != null ? Number(b.order) : 0;
        return oa - ob;
      });
      const validItems = sortedItems.filter(it =>
        it && typeof it.boreholeId === 'string' && it.boreholeId.length > 0
      );
      if (validItems.length === 0) {
        throw new Error('Brak prawidlowych otworow w przekroju');
      }

      const handoffData = {
        items: validItems.map((it, i) => ({
          infoBoreholeId: it.boreholeId,
          currentProjectId: selectedPortalProject.id,
          projectName: selectedPortalProject.title,
          boreholeName: it.boreholeName || '',
          userDataId: it.userDataId || null,
          distance: i === 0 ? '0' : String(it.distance ?? '0'),
          coordX: it.coordX ?? null,
          coordY: it.coordY ?? null,
          coordZ: it.coordZ ?? null,
        })),
        crossSection: fullDoc.settings || {},
      };

      const copyName = `[Portal] ${fullDoc.name || cs.name || 'Przekroj'}`;
      const settings = fullDoc.settings || {};
      const horizontalScale = parseFloat(settings.skalaPozioma) || 100;
      const scale = parseFloat(settings.skalaPionowa) || 70;

      setShowPortalDialog(false);
      navigate('/browse', {
        state: {
          handoffData,
          pendingProject: { name: copyName, scale, horizontalScale },
        },
      });
    } catch (err) {
      console.error('Blad otwierania przekroju z portalu:', err);
      alert(`Nie udalo sie otworzyc przekroju: ${err.message || err}`);
    } finally {
      setOpeningCS(null);
    }
  };

  const handleImportJSON = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      const text = await file.text();
      const jsonData = JSON.parse(text);
      const projectData = deserializeProject(jsonData);

      const projectId = await createProject(
        user.uid,
        file.name.replace('.json', ''),
        projectData
      );
      navigate(`/project/${projectId}`);
    } catch (err) {
      console.error('Blad importu JSON:', err);
      alert('Nie udalo sie zaimportowac pliku JSON.');
    }

    e.target.value = '';
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (err) {
      console.error('Blad wylogowania:', err);
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return '-';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString('pl-PL', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="app-header">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/logo_giode.png" alt="G-ODE" className="h-6" />
            <div>
              <h1 className="text-xl font-bold text-gray-800">Generator przekrojów</h1>
              <p className="text-xs text-gray-400">Przekrój geologiczny</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-sm text-gray-500">{user?.email}</span>
            <button
              onClick={handleLogout}
              className="btn-brand-outline text-sm"
            >
              Wyloguj
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-8">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-lg font-semibold text-gray-700">Twoje przekroje</h2>
          <div className="flex gap-2">
            <button
              onClick={handleCreateProject}
              className="bg-brand-700 text-white px-4 py-2 rounded-md hover:bg-brand-800 text-sm transition"
            >
              + Nowy przekroj
            </button>
            <button
              onClick={handleOpenPortalDialog}
              className="bg-brand-700 text-white px-4 py-2 rounded-md hover:bg-brand-800 text-sm transition"
              title="Wczytaj przekroj zapisany w portalu glownym"
            >
              📥 Z portalu
            </button>
            <button
              onClick={() => fileInputRef.current?.click()}
              className="bg-brand-700 text-white px-4 py-2 rounded-md hover:bg-brand-800 text-sm transition"
            >
              Importuj z JSON
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept=".json"
              onChange={handleImportJSON}
              className="hidden"
            />
          </div>
        </div>

        {loading ? (
          <div className="text-center py-12 text-gray-500">Ladowanie przekrojow...</div>
        ) : projects.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-lg shadow-sm">
            <p className="text-gray-500 mb-4">Nie masz jeszcze zadnych przekrojow.</p>
            <button
              onClick={handleCreateProject}
              className="bg-brand-700 text-white px-6 py-2 rounded-md hover:bg-brand-800 transition"
            >
              Utworz pierwszy przekroj
            </button>
          </div>
        ) : (
          <div className="bg-white rounded-lg shadow-sm divide-y">
            {projects.map(project => (
              <div
                key={project.id}
                className="flex items-center justify-between px-4 py-3 hover:bg-gray-50"
              >
                <div className="flex-1 min-w-0">
                  {renamingId === project.id ? (
                    <form
                      onSubmit={(e) => { e.preventDefault(); handleRename(project.id); }}
                      className="flex gap-2"
                    >
                      <input
                        value={renameValue}
                        onChange={(e) => setRenameValue(e.target.value)}
                        className="border px-2 py-1 rounded text-sm flex-1"
                        autoFocus
                      />
                      <button type="submit" className="text-green-600 text-sm hover:underline">
                        Zapisz
                      </button>
                      <button
                        type="button"
                        onClick={() => setRenamingId(null)}
                        className="text-gray-400 text-sm hover:underline"
                      >
                        Anuluj
                      </button>
                    </form>
                  ) : (
                    <button
                      onClick={() => navigate(`/project/${project.id}`)}
                      className="text-left w-full"
                    >
                      <div className="font-medium text-gray-800 truncate">
                        {project.name}
                      </div>
                      <div className="text-xs text-gray-400">
                        Ostatnia modyfikacja: {formatDate(project.updatedAt)}
                      </div>
                    </button>
                  )}
                </div>

                {renamingId !== project.id && (
                  <div className="flex gap-2 ml-4">
                    <button
                      onClick={() => navigate(`/project/${project.id}`)}
                      className="text-brand-700 text-sm hover:underline"
                    >
                      Otworz
                    </button>
                    <button
                      onClick={() => handleStartRename(project.id, project.name)}
                      className="text-gray-500 text-sm hover:underline"
                    >
                      Zmien nazwe
                    </button>
                    <button
                      onClick={() => handleDeleteProject(project.id, project.name)}
                      className="text-red-500 text-sm hover:underline"
                    >
                      Usun
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </main>

      {showPortalDialog && (
        <div
          className="fixed inset-0 z-[60] bg-black bg-opacity-40 flex items-start justify-center pt-16"
          onClick={() => { if (!openingCS) setShowPortalDialog(false); }}
        >
          <div
            className="bg-white rounded-lg shadow-xl p-6 w-[720px] max-w-[95vw] max-h-[85vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <div className="min-w-0 flex-1">
                <h3 className="text-lg font-bold text-gray-800 truncate">
                  {selectedPortalProject
                    ? `Przekroje w: ${selectedPortalProject.title}`
                    : 'Wczytaj przekroj z portalu'}
                </h3>
                {selectedPortalProject && (
                  <div className="text-xs text-gray-400 font-mono truncate">
                    {selectedPortalProject.id}
                  </div>
                )}
              </div>
              <button
                onClick={() => setShowPortalDialog(false)}
                disabled={!!openingCS}
                className="text-gray-400 hover:text-gray-600 text-xl leading-none disabled:opacity-30"
                title="Zamknij"
              >
                ×
              </button>
            </div>

            {!selectedPortalProject && (
              <>
                <p className="text-sm text-gray-500 mb-3">
                  Wybierz projekt z portalu glownego, aby zobaczyc zapisane w nim przekroje.
                </p>
                <input
                  type="text"
                  value={portalProjectFilter}
                  onChange={(e) => setPortalProjectFilter(e.target.value)}
                  placeholder="Szukaj projektu..."
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm mb-3 focus:outline-none focus:ring-2 focus:ring-brand-700 focus:border-brand-700"
                  autoFocus
                />
                <div className="flex-1 overflow-y-auto border rounded-md">
                  {portalProjectsLoading ? (
                    <div className="p-6 text-center text-sm text-gray-500">Ladowanie projektow...</div>
                  ) : portalProjectsError ? (
                    <div className="p-6 text-center text-sm text-red-600">{portalProjectsError}</div>
                  ) : portalProjects.length === 0 ? (
                    <div className="p-6 text-center text-sm text-gray-500">Brak projektow.</div>
                  ) : (
                    <ul className="divide-y">
                      {portalProjects
                        .filter(p => !portalProjectFilter ||
                          (p.title || '').toLowerCase().includes(portalProjectFilter.toLowerCase()))
                        .map(p => (
                          <li key={p.id}>
                            <button
                              onClick={() => handleSelectPortalProject(p)}
                              className="w-full text-left px-4 py-2 hover:bg-gray-50 text-sm"
                            >
                              <div className="font-medium text-gray-800">{p.title}</div>
                              <div className="text-xs text-gray-400 font-mono">{p.id}</div>
                            </button>
                          </li>
                        ))}
                    </ul>
                  )}
                </div>
              </>
            )}

            {selectedPortalProject && (
              <>
                <button
                  onClick={() => { setSelectedPortalProject(null); setPortalCrossSections([]); }}
                  disabled={!!openingCS}
                  className="text-sm text-brand-700 hover:underline mb-3 self-start disabled:opacity-30"
                >
                  ← Wroc do listy projektow
                </button>
                <div className="flex-1 overflow-y-auto border rounded-md">
                  {portalCSLoading ? (
                    <div className="p-6 text-center text-sm text-gray-500">Ladowanie przekrojow...</div>
                  ) : portalCSError ? (
                    <div className="p-6 text-center text-sm text-red-600">{portalCSError}</div>
                  ) : portalCrossSections.length === 0 ? (
                    <div className="p-6 text-center text-sm text-gray-500">
                      Brak zapisanych przekrojow w tym projekcie.
                    </div>
                  ) : (
                    <ul className="divide-y">
                      {portalCrossSections.map(cs => (
                        <li key={cs.id}>
                          <div className="flex items-center justify-between px-4 py-3 hover:bg-gray-50">
                            <div className="flex-1 min-w-0">
                              <div className="font-medium text-gray-800 truncate">
                                {cs.name}
                              </div>
                              <div className="text-xs text-gray-400">
                                {cs.itemsCount} otwor{cs.itemsCount === 1 ? '' : (cs.itemsCount < 5 ? 'y' : 'ow')}
                                {cs.modified && ` · ${formatDate(cs.modified)}`}
                                {cs.userName && ` · ${cs.userName}`}
                              </div>
                            </div>
                            <button
                              onClick={() => handleOpenPortalCrossSection(cs)}
                              disabled={!!openingCS}
                              className="ml-3 bg-brand-700 text-white px-3 py-1.5 rounded-md hover:bg-brand-800 text-sm transition disabled:opacity-50"
                            >
                              {openingCS === cs.id ? 'Wczytywanie...' : 'Otworz'}
                            </button>
                          </div>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </>
            )}

            <div className="text-xs text-gray-400 mt-3">
              Przekroj zostanie skopiowany jako nowy projekt u Ciebie. Oryginal w portalu pozostaje nietkniety.
            </div>
          </div>
        </div>
      )}

      {showCreateDialog && (
        <div
          className="fixed inset-0 z-[60] bg-black bg-opacity-40 flex items-start justify-center pt-24"
          onClick={() => setShowCreateDialog(false)}
        >
          <div
            className="bg-white rounded-lg shadow-xl p-6 min-w-[360px] max-w-md"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-lg font-bold mb-4 text-gray-800">Nowy przekroj</h3>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                handleConfirmCreate();
              }}
            >
              <label className="block text-sm text-gray-600 mb-2">
                Podaj nazwe nowego przekroju
              </label>
              <input
                type="text"
                value={createValue}
                onChange={(e) => setCreateValue(e.target.value)}
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-700 focus:border-brand-700"
                autoFocus
              />
              <div className="flex justify-end gap-2 mt-5">
                <button
                  type="button"
                  onClick={() => setShowCreateDialog(false)}
                  className="px-4 py-2 rounded-md text-sm text-gray-600 hover:bg-gray-100"
                >
                  Anuluj
                </button>
                <button
                  type="submit"
                  disabled={!createValue.trim()}
                  className="bg-brand-700 text-white px-4 py-2 rounded-md hover:bg-brand-800 text-sm transition disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Utworz
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
